<template>
  <div class="new-item">
    <h3 class="text-center">create new enrol profile</h3>
    
    <VeeForm v-slot="{ invalid }">
      <form autocomplete="off">

        <div class="form-group">
          <label for="enrolId">enrol id</label>
          <Field
            name="enrolId" 
            :rules="{
              required:true, 
              validEnrolId:true, 
              nonRepeatedId: {curIdList: enrolProfileIdList} 
            }" 
            v-slot="{ field, errors }"
          >
            <input type="text" class="form-control" name="enrolId" placeholder="i.e. profile name" v-bind="field" v-model="enrolId">
            <div class="error">{{errors[0]}}</div>
          </Field>
        </div>

        <div class="form-group">
          <label for="description">description</label>
          <Field name="description" rules="required|min:3" v-slot="{ field, errors }">
            <input type="text" class="form-control" name="description" v-bind="field" v-model="description">
            <div class="error">{{errors[0]}}</div>
          </Field>
        </div>

        <SectionHeader title="actility" class="mt-5"/>
        
        <div class="form-group">
          <label for="baseUrl">base url</label>
          <Field name="baseUrl" rules="required|url" v-slot="{ field, errors }">
            <input type="text" class="form-control" name="baseUrl" v-bind="field" v-model="actility.baseUrl">
            <div class="error">{{errors[0]}}</div>
          </Field>
        </div>

        <div class="form-group">
          <label for="username">username</label>
          <Field name="username" rules="required|email" v-slot="{ field, errors }">
            <input type="text" class="form-control" name="username" v-bind="field" v-model="actility.username">
            <div class="error">{{errors[0]}}</div>
          </Field>
        </div>

        <div class="form-group">
          <label for="password">password</label>
          <Field name="password" rules="required|min:3" v-slot="{ field, errors }">
            <input type="text" class="form-control" name="password" v-bind="field" v-model="actility.password">
            <div class="error">{{errors[0]}}</div>
          </Field>
        </div>

        <div class="form-group">
          <label for="profileName">profileName</label>
          <Field name="profileName" rules="required|min:3" v-slot="{ field, errors }">
            <input type="text" class="form-control" name="profileName" v-bind="field" v-model="actility.profileName">
            <div class="error">{{errors[0]}}</div>
          </Field>
        </div>

        <div class="form-group">
          <label for="n2nUrl">n2n url</label>
          <Field name="n2nUrl" rules="required|url" v-slot="{ field, errors }">
            <input type="text" class="form-control" name="n2nUrl" v-bind="field" v-model="actility.n2nUrl">
            <div class="error">{{errors[0]}}</div>
          </Field>
        </div>

        <div class="form-group">
          <label for="devProfileClassA">device profile class A</label>
          <Field vid="devProfileClassA" name="devProfileClassA" :rules="{ required: !actility.devProfileClassC, min: 3 }" v-slot="{ field, errors }">
            <input type="text" class="form-control" name="devProfileClassA" v-bind="field" v-model="actility.devProfileClassA">
            <div class="error">{{errors[0]}}</div>
          </Field>
        </div>

        <div class="form-group">
          <label for="devProfileClassA">device profile class C</label>
          <Field vid="devProfileClassC" name="devProfileClassC" :rules="{ required: !actility.devProfileClassA, min: 3 }" v-slot="{ field, errors }">
            <input type="text" class="form-control" name="devProfileClassC" v-bind="field" v-model="actility.devProfileClassC">
            <div class="error">{{errors[0]}}</div>
          </Field>
        </div>

        <div class="form-group">
          <label for="networkSubscription">connectivity plan</label>
          <Field name="networkSubscription" rules="required|min:3" v-slot="{ field, errors }">
            <input type="text" class="form-control" name="networkSubscription" v-bind="field" v-model="actility.networkSubscription">
            <div class="error">{{errors[0]}}</div>
          </Field>
        </div>

        <button 
          class="btn btn-info new-device-btn"
          type="submit" 
          @click.prevent="doCreate()" 
          :disabled="invalid || !canCreate"
>
          <font-awesome-icon icon="plus"/> create enrol profile</button>

        <button type="button" class="btn btn-secondary" @click="cancel()">Cancel</button>

      </form>
    </VeeForm>
    <Loading v-if="httpBusy"/>
    <Error :label="error"/>
    <Success :label="success"/>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import Loading from '@/components/Loading.vue';
import Error from '@/components/Error.vue';
import Success from '@/components/Success.vue';
import SectionHeader from '@/components/SectionHeader.vue';
import { ApiClient, curApiClient } from '@/services/ApiClient';
import { EnrolProfileActility } from '@/types';

interface Props {
  enrolProfileIdList: string[];
}

withDefaults(defineProps<Props>(), {
  enrolProfileIdList: () => []
});

const emit = defineEmits<{
  cancel: [];
}>();

// our model
const error = ref<string>('');
const success = ref<string>('');

// form data
const enrolId = ref<string>('');
const description = ref<string>('');
const actility = ref<EnrolProfileActility>({} as EnrolProfileActility);

const httpBusy = ref<boolean>(false);

// internal dependencies
const apiClient: ApiClient = curApiClient;

onMounted(async () => {
  // Component mounted
});

const canCreate = computed<boolean>(() => {
  return !httpBusy.value;
});

const doCreate = async () => {
  httpBusy.value = true;
  error.value = '';

  const newEnrolProfileReq = {
    enrolId: enrolId.value,
    description: description.value,
    type:'actility' as const,
    actility: Object.assign({}, actility.value)
  };

  const reply = await apiClient.createEnrolProfile(newEnrolProfileReq);

  httpBusy.value = false;
  
  if(reply.errorMsg)
    return error.value = `unable to create enrol profile: ${reply.errorMsg}`;

  setSuccess(`enrol profile ${enrolId.value} created successfully`);
};

const setSuccess = (str: string) => {
  success.value = str;
  setTimeout(() => emit('cancel'), 2000);
};

const cancel = () => {
  emit('cancel');
};
</script>

<style scoped lang="scss">
@use "@/variables.scss" as *;

label, .form-check-title {
  font-weight: bold;
  color: $bgColour;
}
.form-check > label {
  font-weight:normal;
  color: black;
}

input::placeholder, textarea::placeholder, .disabled {
  color: $linkColour;
}

.new-item {
  text-align: center;
  max-width: 450px;
  margin: 0 auto;
}

.error {
  color: red;
  font-size: 0.75em;
  font-weight: bold;
  margin-top:10px
}

form button {
  margin: 3px;
}

.explanation {
  font-size: 0.8rem;
  font-style: italic;
  color: $linkColour;
}
</style>